"use client";
import axios from "axios";
import { RefObject, useEffect, useState } from "react";
// import countries from "i18n-iso-countries";
import { useRouter } from "@/i18n/navigation";

import SVG from "../../pages/SVG";
import Dropdown from "../Dropdown";
import "./index.css";
// import { CURRENCY } from "@/constants/countrycodes";
import { Dayjs } from "dayjs";
import { components, SingleValueProps } from "react-select";

import { getFilteredParams } from "@/app/common/utils/getFilteredParams";
import { useWalletService } from "@/app/context/walletServiceContext/walletServiceContext";
import { useToast } from "@/app/hooks/toast/useToast";
import {
  API_DATE_FORMAT,
  MAX_TRAVELER_COUNT,
  SEARCH_PARAMS,
  getTravelerOptions,
} from "@/app/utils/constants/constants";
import getDeviceType from "@/app/utils/device/getDeviceType";
import { getDatesDiff, redirect } from "@/app/utils/services/helper/helper";
import {
  getPartnerCode,
  setLastSearchRequestToLocalStorage,
} from "@/app/utils/services/service/localStorageService";
import {
  Destination,
  GeoLocation,
  // HotelRequest,
  OptionType,
  SelectOption,
} from "@/types";
import { useTheme } from "next-themes";
import { CustomDatePicker } from "../DatePicker";
import { useDatePickerContext } from "../DatePicker/DatePickerContext";
import CustomSelect from "../Dropdown/CustomDropDrown";
import { TravelIcon, TravelIconDark } from "../Icons";
import useSetSearchParams from "./useSetSearchParams";
import { useSearchParams } from "next/navigation";
import { useLocale, useTranslations } from "next-intl";

// countries.registerLocale(require("i18n-iso-countries/langs/en.json"));

type Props = {
  searchRef?: RefObject<HTMLDivElement>;
  calendarRef?: RefObject<HTMLDivElement>;
  isHomePage?: boolean;
  destination: Destination;
  setDestination: (val: Destination) => void;
  isCustomClass?: boolean;
};

const Search: React.FC<Props> = (props) => {
  // const [loading, setLoading] = useState(true);
  const { showToast } = useToast();
  const [searchLoading, setSearchLoading] = useState(false);
  const [hotelOptions, setHotelOptions] = useState<Destination[]>([]);
  const [error, setError] = useState(false);
  const { selectedDate } = useDatePickerContext();
  const t = useTranslations("HomePageHotels");
  const otherT = useTranslations("Other");
  const locale = useLocale();
  const { resolvedTheme } = useTheme();

  const {
    searchRef,
    calendarRef,
    destination,
    setDestination,
    isHomePage = true,
    isCustomClass,
  } = props;

  const router = useRouter();
  const searchParams = useSearchParams();
  const params = new URLSearchParams(searchParams.toString());

  const {
    travelersTotal,
    setTravelersTotal,
    travellerOptions,
    setTravallerOptions,
  } = useSetSearchParams({
    searchParams,
    router,
    isHomePage,
    destination,
    setDestination,
  });
  const { userDetails, getConnectedWallet } = useWalletService();

  useEffect(() => {
    // getHotelGroups();
    fetchPlaces();
  }, []);

  useEffect(() => {
    handleDateChange(selectedDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  const handleFormSubmit = async (e: any) => {
    // handle form submit
    e.preventDefault();

    const isError = validateSearchRequest();

    if (!isError) {
      setSearchLoading(true);
      const hotelSearchRequest = createSearchRequest();

      if (userDetails?.walletId) {
        // private search
        await createHotelRequestToken(
          userDetails?.walletId?.trim(),
          hotelSearchRequest
        );
      } else {
        await searchForHotels(hotelSearchRequest);
      }
    }
  };

  const fetchPlaces = async () => {
    try {
      const res = await axios.get(`/api/places?lang=${locale}`);

      const places = new Set();

      res?.data?.places.forEach((place: any) => {
        places.add({
          id: place?.id,
          destination: place?.destination,
          hotelCount: 0,
          country: place?.country,
          city: place?.city,
          label: place?.destination,
          value: (place?.hotelCount ?? 0).toString(),
        });
      });
      setHotelOptions(res?.data?.places.slice(0, 5) || []);
    } catch (err) {
      console.log("Error fetching places: ", err);
      setHotelOptions([]);
    }
  };

  const validateSearchRequest = () => {
    if (selectedDate.length) {
      const isGreater = getDatesDiff(selectedDate, 30);

      if (isGreater) {
        setError(true);
        return true;
      }
    }

    return false;
  };

  const createSearchRequest = () => {
    const geolocation: GeoLocation = {
      lat: undefined,
      lng: undefined,
    };

    const hotelSearchRequest = {
      id: destination?.id,
      from:
        selectedDate[0] && selectedDate[0].format(API_DATE_FORMAT).toString(),
      to: selectedDate[1] && selectedDate[1].format(API_DATE_FORMAT).toString(),
      numberOfChildren: Number(travellerOptions[1].value),
      numberOfAdults: Number(travellerOptions[0].value),
      numberOfInfants: Number(travellerOptions[2].value),
      city: destination?.city,
      country: destination?.country,
      currency: "USD",
      location: geolocation,
      locationName: undefined,
      destination: destination?.destination,
      wallet: userDetails?.walletId?.trim(),
    };

    return hotelSearchRequest;
  };

  const searchForHotels = async (hotelRequest: any) => {
    try {
      const body = {
        ...hotelRequest,
        partnerCode: getPartnerCode(),
      };
      setLastSearchRequestToLocalStorage(hotelRequest, locale);
      const { data } = await axios.post(`/api/search?lang=${locale}`, body);
      const filteredParams = getFilteredParams(searchParams);
      window.location.href = `/results?searchId=${data?.searchId}&${filteredParams.toString()}&title=${hotelRequest?.destination}`;
      setSearchLoading(false);
    } catch (err: any) {
      setSearchLoading(false);
      if (err?.response?.data?.error) {
        showToast(err.response.data.error, "error");
      } else if (err?.response?.statusText) {
        showToast(err.response.statusText, "error");
      } else {
        showToast("An unexpected error occurred.", "error");
      }
    }
  };

  const createHotelRequestToken = async (
    _walletAddress: string,
    hotelRequest: any
  ) => {
    try {
      if (!hotelRequest.country) {
        hotelRequest.country = undefined;
      }
      // const headers = {};
      const data = {
        ...hotelRequest,
        partnerCode: getPartnerCode(),
        connectedWallet: getConnectedWallet(),
      };

      const url = `/api/generateToken?lang=${locale}`;

      const res = await axios.post(url, data, { withCredentials: true });
      setSearchLoading(false);

      if (res?.data?.tokenId) {
        // redirect to the search result page &  save to local storage
        setLastSearchRequestToLocalStorage(hotelRequest, locale);
        const tokenId = res?.data?.tokenId;
        const filteredParams = getFilteredParams(searchParams);
        window.location.href = `/results?tokenId=${tokenId}&${filteredParams.toString()}&title=${hotelRequest?.destination}`;
      }
    } catch (err) {
      setSearchLoading(false);
      console.error(err);
    }
  };

  const handleTravelOpClick = (op: string, label: string) => {
    const newOps = [...travellerOptions];

    const elem: OptionType | undefined = newOps.find(
      (o: any) => o.label === label
    );

    if (elem) {
      if (
        (op == "minus" && Number(elem?.value) > 0) ||
        (op == "plus" && travelersTotal < MAX_TRAVELER_COUNT)
      ) {
        elem.value =
          op == "minus"
            ? (Number(elem?.value) - 1).toString()
            : (Number(elem?.value) + 1).toString();
        setTravallerOptions(newOps);
        setTravelersTotal(
          op == "minus" ? travelersTotal - 1 : travelersTotal + 1
        );
      }
    }
  };

  const formatTravel = (props: SelectOption) => {
    const isDisabled =
      travelersTotal <= 0 ||
      props?.value === "0" ||
      (props?.label === otherT("adults") && props?.value === "1");

    return (
      <div
        className="flex items-center justify-between border-b border-zinc-500 gap-2 capitalize sm:p-2 max-w-[1280px] last:border-0 text-sleap-gray-950 dark:text-white"
        id="let traveler of TRAVELER_TYPES"
      >
        <div className="flex flex-col">
          <div className="label font-medium text-base font-inter text-sleap-gray-950 dark:text-lightwhite leading-6">
            {props?.label}
          </div>
          <div className="font-medium text-xs text-sleap-gray-500">
            {/* @ts-ignore */}
            {t("yearsOld", { old: props.description })}{" "}
          </div>
        </div>
        <div className="flex gap-3 py-2 items-center">
          <button
            type="button"
            className="cursor-pointer sm:p-0 p-2 sm:bg-transparent bg-[#E4E4E7] dark:bg-sleap-gray-600"
            disabled={isDisabled}
            onClick={() => handleTravelOpClick("minus", props?.label)}
          >
            <SVG
              alt="minus-icon"
              twClasses="icon-wrapper dark:block hidden"
              src="/assets/v2/icons/minus-icon.svg"
            />
            <SVG
              alt="minus-icon"
              twClasses="icon-wrapper dark:hidden block"
              src="/assets/v2/icons/minus-icon-dark.svg"
            />
          </button>
          <div className="text-lg font-inter font-bold"> {props?.value} </div>
          <button
            type="button"
            className="cursor-pointer sm:p-0 p-2 sm:bg-transparent bg-[#E4E4E7] dark:bg-sleap-gray-600"
            disabled={travelersTotal >= MAX_TRAVELER_COUNT}
            onClick={() => handleTravelOpClick("plus", props?.label)}
          >
            <SVG
              src="/assets/v2/icons/plus-icon.svg"
              alt="plus-icon"
              twClasses="icon-wrapper dark:block hidden"
            />
            <SVG
              src="/assets/v2/icons/plus-icon-dark.svg"
              alt="plus-icon"
              twClasses="icon-wrapper dark:hidden block"
            />
          </button>
        </div>
      </div>
    );
  };

  const travllerValueContainer = ({
    // eslint-disable-next-line no-unused-vars
    children,
    ...props
  }: SingleValueProps<any>) => {
    const totalTravelers: any = props?.options?.reduce((total, num) => {
      return { value: Number(total.value) + Number(num.value) };
    });

    return (
      <components.SingleValue {...props}>
        {totalTravelers?.value > 0 ? totalTravelers?.value : "Travelers"}
      </components.SingleValue>
    );
  };

  const handleDateChange = (date: [Dayjs | null, Dayjs | null]) => {
    if (date && date.length > 0) {
      setError(false);

      if (isHomePage) {
        params.set(
          SEARCH_PARAMS.CHECKIN,
          date[0]?.format(API_DATE_FORMAT) || ""
        );
        params.set(
          SEARCH_PARAMS.CHECKOUT,
          date[1]?.format(API_DATE_FORMAT) || ""
        );
        redirect(params);
      }
    }
  };

  const handleDestinationChange = (op: OptionType) => {
    const { city, destination, id } = op || {};

    if (destination) {
      params.set(SEARCH_PARAMS.CITY, city);
      params.set(SEARCH_PARAMS.DESTINATION, destination);
      params.set(SEARCH_PARAMS.ID, id);
    } else {
      params.delete(SEARCH_PARAMS.CITY);
      params.delete(SEARCH_PARAMS.DESTINATION);
      params.delete(SEARCH_PARAMS.ID, id);
    }

    redirect(params);
  };

  // const handleCurrencyChange = (op: OptionType) => {
  //   const { label } = op;
  //   params.set(SEARCH_PARAMS.CURRENCY, label);

  //   redirect(params);
  //   setCurrency({ ...op });
  // };

  const handleTravellerChange = (_op: OptionType) => {
    params.set(SEARCH_PARAMS.ADULTS, travellerOptions[0].value);
    params.set(SEARCH_PARAMS.CHILDREN, travellerOptions[1].value);
    params.set(SEARCH_PARAMS.INFANTS, travellerOptions[2].value);

    redirect(params);
  };

  return (
    <form className="flex justify-center " onSubmit={handleFormSubmit}>
      <div
        id="search-box"
        ref={searchRef}
        className={`flex sm:p-3 py-3 w-full sm:m-auto sm:mx-0 flex-col gap-3 rounded-2xl  ${!isCustomClass ? "search-form p-2" : "!px-0 inner-pages-search"} bg-lightwhite dark:bg-sleap-gray-950 text-sleap-gray-950 dark:text-white `}
      >
        <div className="search-bar-container grid flex-col sm:grid-cols-2 grid-cols-1 md:grid gap-3 ">
          <CustomSelect
            placeHolder={t("whereAreYouTravelling")}
            selectedValue={destination}
            setSelectedValue={setDestination}
            _options={hotelOptions}
            isSearchable={true}
            onOptionChange={handleDestinationChange}
          />
          <div ref={calendarRef}>
            <CustomDatePicker />
          </div>
          <Dropdown
            travellerOptions={travellerOptions}
            placeholder={otherT("travellers")}
            className="md:w-full flex flex-row items-center rounded-lg gap-3 py-1 px-4 border-[1px] dark:border-input border-sleap-gray-400 bg-lightwhite dark:bg-sleap-gray-950 dark:hover:border-white h-14 text-center hover:border-sleap-gray-600 text-sleap-gray-800 dark:text-white "
            iconComp={
              <div className="w-8 ">
                <span className="dark:block hidden">
                  <TravelIcon />
                </span>
                <span className="dark:hidden block">
                  <TravelIconDark />
                </span>
              </div>
            }
            dropdownIndicator={true}
            options={travellerOptions}
            formatOptionLabel={formatTravel}
            isClearable={false}
            isSearchable={false}
            customComponents={{
              SingleValue: travllerValueContainer,
            }}
            selectedOption={{ value: travelersTotal }}
            closeMenuOnSelect={false}
            onClick={isHomePage ? handleTravellerChange : () => {}} //@ts-ignore
          />
          <div
            className={`relative ${!destination?.city || error ? "group" : ""}`}
          >
            <button
              className="search-button text-base font-medium font-sans py-3 md:py-4 px-5 md:px-6 flex-shrink-0 bg-sleap-purple-300 hover:bg-sleap-purple-300 text-white rounded-lg sm:w-fit w-full"
              disabled={!destination?.city || searchLoading || error}
            >
              {!searchLoading ? (
                <>
                  {isCustomClass &&
                  getDeviceType().isMobileType &&
                  resolvedTheme === "dark" ? (
                    <SVG
                      alt="magnifier"
                      src="/assets/v2/icons/magnifier-dark.svg"
                      twClasses="icon-wrapper"
                    />
                  ) : (
                    <SVG
                      alt="magnifier"
                      src="/assets/v2/icons/magnifier.svg"
                      twClasses="icon-wrapper"
                    />
                  )}
                  <div className="break-keep text-nowrap">
                    {isCustomClass && getDeviceType().isMobileType
                      ? otherT("updateSearch")
                      : otherT("search")}
                  </div>
                </>
              ) : (
                <div className="w-full flex justify-center items-center h-6">
                  <span className="loader !h-9 !w-9"></span>
                  <span className="loader-size"></span>
                </div>
              )}
            </button>
            {!isCustomClass && (
              <div className="absolute md:bottom-full w-44 -bottom-10 lg:left-1/2 md:-right-28 right-0 transform -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 group-hover:translate-y-2 transition-all duration-300 flex flex-col items-center">
                <div className="bg-gray-900 text-white py-2 px-3 text-xs rounded-md shadow-lg">
                  {t("enterDestination")}
                </div>
                <div className="bg-gray-900 h-2 w-2 transform rotate-45 -mt-1"></div>
              </div>
            )}
          </div>{" "}
        </div>
      </div>
    </form>
  );
};

export default Search;
